/* :Page:QuickRenewal: CPR plate number page */
import React, { useState, useEffect } from "react";
import { RENEW_POLICY_LOGGED_OUT, RENEW_POLICY_LOGGED_IN } from "../../../../hooks/useQuickRenewalFlow";
import RenewMotorLayout from "../renewMotorLayout";
import useValidation from "../../../../hooks/useValidation";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import { useTranslation } from "react-i18next";
import useMotorForm from "../../../../hooks/useMotorFormData";
import Input from "../../../../components/common/Input/Input";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { toast } from "sonner";
import useQuickRenewalFlow from "../../../../hooks/useQuickRenewalFlow";
import { clearApplyPromoState } from "../../../../redux/features/promoCode/promoCodeSlice";
import { PROMO_CODE } from "../../../../components/newMotorPolicy/veiwSummaryPopup/viewSummaryPopup";
import useLanguageTranslate from "../../../../../src/hooks/useLanguageTranslate";
import { isLoggedIn } from "../../../../config/config";
import { getPolicies } from "../../../../redux/features/myPolicySlice";
import { motorRenewalPremium } from "../../../../redux/features/motor/renewalPremiumSlice";
import { getClientSource } from "../../../../utils/getClientSource";

export const RENEW_POLICY_CPR_PLATE_NUMBER = "RENEW_POLICY_CPR_PLATE_NUMBER";

const QuickRenewalCprPlateNumberPage = () => {
  const dispatch = useDispatch();

  const { handleFlowChange } = useQuickRenewalFlow();
  const { errors, validateFields } = useValidation();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ cprNumber: "", plateNumber: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const translate = useLanguageTranslate();
  
  const userLoggedIn = isLoggedIn();

  const { saveMotorDataInRedux } = useMotorForm(
    RENEW_POLICY_CPR_PLATE_NUMBER,
    setFormData
  );

  const { removeMotorFormData } = useMotorForm(
    RENEW_POLICY_CPR_PLATE_NUMBER,
    setFormData
  );

  const { removeMotorFormPromoCodeData } = useMotorForm(
    PROMO_CODE,
    setFormData
  );

  const { saveMotorDataInRedux: saveCprPlateData } = useMotorForm(
    RENEW_POLICY_CPR_PLATE_NUMBER,
    setFormData
  );

  const validateForm = (data) => {
    let validationConfig = [
      {
        name: "cprNumber",
        validations: ["required"],
      },
      {
        name: "plateNumber",
        validations: ["required"],
      },
    ];
    let isFormValid = validateFields(validationConfig, data ? data : formData);
    return isFormValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Remove pinId and masked phonenumber from state if CPR is changed
    removeMotorFormData();
    let update = { ...formData, [name]: value };

    setFormData(update);
    validateForm(update);
  };

  useEffect(() => {
    if (userLoggedIn) {
      handleFlowChange(RENEW_POLICY_LOGGED_IN, 0);
    } else {
      handleFlowChange(RENEW_POLICY_LOGGED_OUT, 0);
    }
  }, [userLoggedIn]);

  const continueClickHanlder = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Clear promo code
      await removeMotorFormPromoCodeData();
      await dispatch(clearApplyPromoState());

      // Save form data
      saveCprPlateData({ ...formData });

      // Both logged-in and logged-out users: fetch policies directly
      // (no OTP step). Business team requested reverting the OTP gate added
      // in commit b712a17.
      const getPoliciesPayload = {
        Cpr: formData.cprNumber,
        Channel: getClientSource(),
      };

      const policiesAction = await dispatch(getPolicies(getPoliciesPayload));
      const policiesResult = unwrapResult(policiesAction);

      if (policiesResult && policiesResult.length > 0) {
        const renewablePolicy = policiesResult.find(
          (policy) =>
            policy?.IsRenewable == true &&
            policy?.Renewed !== "Yes" &&
            policy?.ProductCode === "0101" &&
            policy?.RegistrationNumber == formData.plateNumber
        );

        if (renewablePolicy) {
          // Fetch the renewal premium then advance the in-component step
          // renderer to the Review & Confirm page (rendered by
          // QuickRenewalMotorPolicy, not a separate route).
          const premiumPayload = {
            PolicyNumber: renewablePolicy?.PolicyNumber,
            Channel: getClientSource(),
          };
          const premiumAction = await dispatch(motorRenewalPremium(premiumPayload));
          unwrapResult(premiumAction);
          handleFlowChange(RENEW_POLICY_LOGGED_OUT, 1);
        } else {
          const policyWithPlate = policiesResult.find(
            (policy) => policy?.RegistrationNumber == formData.plateNumber
          );

          if (policyWithPlate) {
            toast.error(
              translate(
                `The Registration number ${formData.plateNumber} is not due for renewal yet.`,
                `رقم التسجيل ${formData.plateNumber} غير مستحق للتجديد بعد.`
              )
            );
          } else {
            toast.error(
              translate(
                `No policy found with plate number ${formData.plateNumber}.`,
                `لم يتم العثور على بوليصة برقم اللوحة ${formData.plateNumber}.`
              )
            );
          }
        }
      } else {
        toast.error(
          translate(
            "No policies found for this account.",
            "لم يتم العثور على بوالص لهذا الحساب."
          )
        );
      }
    } catch (e) {
      console.log(e);
      toast.error(translate(e?.ErrorMessageEn || "Failed to fetch policies", e?.ErrorMessageAr || "فشل جلب البوالص"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <RenewMotorLayout
      show_close_btn
      is_hide_back_btn={true}
      btn_onClick={continueClickHanlder}
      isLoading={isSubmitting}
      btn_disabled={isSubmitting}
    >
      <div className="max-w-[100%] w-[500px] mx-auto bg-white max767:p-5 max767:pt-0">
        <TitleDescription
          title={t("motorInsurance.renewalPolicyFlow.cprNoPage.quickRenewal")}
          description={t(
            "motorInsurance.renewalPolicyFlow.cprNoPage.quickRenewalDesc"
          )}
        />
        <Input
          name="cprNumber"
          type="text"
          label={t("motorInsurance.renewalPolicyFlow.cprNoPage.cprNo")}
          value={formData?.cprNumber}
          error={errors?.cprNumber}
          handleChange={handleChange}
        />
        <Input
          name="plateNumber"
          type="number"
          label={t("motorInsurance.renewalPolicyFlow.cprNoPage.plateNo")}
          value={formData?.plateNumber}
          error={errors?.plateNumber}
          handleChange={handleChange}
        />
      </div>
    </RenewMotorLayout>
  );
};

export default QuickRenewalCprPlateNumberPage;
